import React, { useCallback, useEffect, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row } from 'react-bootstrap';
import ConditionalValueInput from '../../../components/ConditionalValueInput';
import { useModal } from '../../../context/ModalContext';
import type { ChangeFormField, ChangeFormFieldType, ChangePeriodMode } from '../../../types/changes';
import { getApiErrorMessage } from '../../../utils/apiError';
import {
  changeFormFieldDeleteBlockReason,
  changeFormFieldShowsDeleteButton,
} from '../changeFormFieldDelete';
import {
  FORM_FIELD_TYPE_LABELS,
  FORM_PERIOD_MODE_LABELS,
  FORM_WEEKDAY_LABELS,
  defaultYesNoStoredValue,
  isDateLikeFieldType,
  operatorsForConditionalFieldType,
  CHANGE_OWNER_PICKER_MODE_LABELS,
  changeOwnerPickerMode,
  changeOwnerPickerOptionsFromMode,
  parseOptionsText,
  storedYesNoValue,
  type ChangeOwnerPickerMode,
  yesNoChoices,
  yesNoOptionsFromPair,
  yesNoPairFromOptions,
} from '../changeFormSchema';
import { changesApi } from '../../../services/changesApi';
import type { ChangeCatalogItem, ChangeTypePolicy } from '../../../types/changes';
import {
  buildChangeTypeConditionalChoices,
  conditionalChoicesForFormField,
  type ChangeFormConditionalCatalogs,
} from '../changeFormConditionalOptions';
import type { ChangeFormFieldBuilderApi } from '../changeFormFieldBuilderApi';

export type { ChangeFormFieldBuilderApi } from '../changeFormFieldBuilderApi';

const FIELD_TYPES = Object.keys(FORM_FIELD_TYPE_LABELS) as ChangeFormFieldType[];
const EXTRA_FIELD_TYPES = FIELD_TYPES.filter((type) => type !== 'immediate_manager_pick');

interface Props {
  heading: string;
  description: string;
  api: ChangeFormFieldBuilderApi;
  embedded?: boolean;
  allowSkipChecklist?: boolean;
  allowDateRevisionHistory?: boolean;
  formTabs?: string[];
}

interface SortableRowProps {
  field: ChangeFormField;
  onEdit: (field: ChangeFormField) => void;
  onDelete: (field: ChangeFormField) => void;
}

const fieldMeta = (field: ChangeFormField) => (
  <>
    {field.kind === 'native'
      ? field.field_type === 'immediate_manager_pick'
        ? FORM_FIELD_TYPE_LABELS.immediate_manager_pick
        : 'Sistema'
      : FORM_FIELD_TYPE_LABELS[field.field_type]}
    {field.col_span === 6 ? ' · meia linha' : ' · linha inteira'}
    {field.is_required ? ' · obrigatório' : ''}
    {field.skips_auto_checklist ? ' · dispensa checklist' : ''}
    {field.weekday_constraint != null && FORM_WEEKDAY_LABELS[field.weekday_constraint]
      ? ` · ${FORM_WEEKDAY_LABELS[field.weekday_constraint]}`
      : ''}
    {field.date_revision_limit
      ? ` · histórico ${field.date_revision_limit}x`
      : ''}
    {field.conditional_field_id ? ' · condicional' : ''}
    {field.section_name ? ` · aba ${field.section_name}` : ''}
    {field.native_key === 'change_owner_id'
      ? ` · ${CHANGE_OWNER_PICKER_MODE_LABELS[changeOwnerPickerMode(field)]}`
      : ''}
    {field.select_search_enabled ? ' · pesquisa no select' : ''}
  </>
);

const SortableRow: React.FC<SortableRowProps> = ({ field, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
  });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
      }}
      className="border rounded p-2 mb-2 bg-white"
    >
      <div className="d-flex align-items-center gap-2">
        <button
          type="button"
          className="btn btn-light btn-sm"
          style={{ cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none' }}
          aria-label={`Reordenar ${field.label}`}
          title="Segure e arraste para cima ou para baixo"
          {...attributes}
          {...listeners}
        >
          ⋮⋮
        </button>
        <div className="flex-grow-1">
          <strong>{field.label}</strong>
          <div className="small text-muted">{fieldMeta(field)}</div>
        </div>
        {field.is_locked ? <Badge bg="secondary">sistema</Badge> : null}
        {!field.is_visible ? <Badge bg="warning">oculto</Badge> : null}
        <Button size="sm" variant="outline-primary" onClick={() => onEdit(field)}>
          Editar
        </Button>
        {changeFormFieldShowsDeleteButton(field) ? (
          <Button size="sm" variant="outline-danger" onClick={() => onDelete(field)}>
            Excluir
          </Button>
        ) : null}
      </div>
    </div>
  );
};

const blankExtra = () => ({
  label: '',
  field_type: 'text_short' as ChangeFormFieldType,
  period_mode: 'range' as ChangePeriodMode,
  weekday_constraint: '' as number | '',
  date_revision_limit: '' as number | '',
  is_required: false,
  help_text: '',
  optionsText: '',
  yesLabel: 'Sim',
  noLabel: 'Não',
  col_span: 12,
  section_name: '',
  is_visible: true,
  skips_auto_checklist: false,
  conditional_field_id: 0,
  conditional_operator: 'equals',
  conditional_value: '',
  default_value: '',
  ownerPickerMode: 'logged_user' as ChangeOwnerPickerMode,
  select_search_enabled: false,
});

const ChangeFormFieldBuilder: React.FC<Props> = ({
  heading,
  description,
  api,
  embedded = false,
  allowSkipChecklist = false,
  allowDateRevisionHistory = false,
  formTabs,
}) => {
  const { showAlert, showConfirm } = useModal();
  const [fields, setFields] = useState<ChangeFormField[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<ChangeFormField | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(blankExtra());
  const [activeId, setActiveId] = useState<number | null>(null);
  const [conditionalCatalogs, setConditionalCatalogs] = useState<ChangeFormConditionalCatalogs>({
    typeOptions: [],
    categories: [],
    requesterAreas: [],
  });
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const dragging = fields.find((item) => item.id === activeId) || null;
  const hasFormTabs = (formTabs?.length ?? 0) > 0;

  const load = useCallback(async () => {
    const data = await api.load();
    setFields(data.items || []);
  }, [api]);

  useEffect(() => {
    void load().catch((err: unknown) => {
      setError(getApiErrorMessage(err, 'Não foi possível carregar o formulário.'));
    });
  }, [load]);

  useEffect(() => {
    void (async () => {
      const [typesResult, categoriesResult, areasResult] = await Promise.allSettled([
        changesApi.listTypePolicies({ only_active: true }),
        changesApi.listCatalog('categories', { only_active: true }),
        changesApi.listCatalog('requester-areas', { only_active: true }),
      ]);
      const policies: ChangeTypePolicy[] =
        typesResult.status === 'fulfilled' ? typesResult.value.items || [] : [];
      const categories: ChangeCatalogItem[] =
        categoriesResult.status === 'fulfilled' ? categoriesResult.value.items || [] : [];
      const requesterAreas: ChangeCatalogItem[] =
        areasResult.status === 'fulfilled' ? areasResult.value.items || [] : [];
      setConditionalCatalogs({
        typeOptions: buildChangeTypeConditionalChoices(policies),
        categories,
        requesterAreas,
      });
    })();
  }, []);

  const openCreate = () => {
    setCreating(true);
    setEditing(null);
    setDraft(blankExtra());
  };

  const openEdit = (field: ChangeFormField) => {
    setCreating(false);
    setEditing(field);
    const pair = yesNoPairFromOptions(field.options);
    setDraft({
      label: field.label,
      field_type: field.field_type,
      period_mode: (field.period_mode || 'range') as ChangePeriodMode,
      weekday_constraint: field.weekday_constraint ?? '',
      date_revision_limit: field.date_revision_limit ?? '',
      is_required: field.is_required,
      help_text: field.help_text || '',
      optionsText: (field.options || []).join('\n'),
      yesLabel: pair.yesLabel,
      noLabel: pair.noLabel,
      col_span: field.col_span === 6 ? 6 : 12,
      section_name: field.section_name || '',
      is_visible: field.is_visible,
      skips_auto_checklist: Boolean(field.skips_auto_checklist),
      conditional_field_id: field.conditional_field_id || 0,
      conditional_operator: field.conditional_operator || 'equals',
      conditional_value: field.conditional_value || '',
      default_value: field.default_value || '',
      ownerPickerMode: changeOwnerPickerMode(field),
      select_search_enabled: Boolean(field.select_search_enabled),
    });
  };

  const payloadFromDraft = () => ({
    label: draft.label.trim(),
    ...(creating || editing?.kind === 'extra' ? { field_type: draft.field_type } : {}),
    period_mode:
      draft.field_type === 'period' || draft.field_type === 'period_datetime'
        ? draft.period_mode
        : null,
    weekday_constraint: isDateLikeFieldType(draft.field_type)
      ? draft.weekday_constraint === ''
        ? null
        : Number(draft.weekday_constraint)
      : null,
    ...(allowDateRevisionHistory
      ? {
          date_revision_limit: isDateLikeFieldType(draft.field_type)
            ? draft.date_revision_limit === ''
              ? null
              : Number(draft.date_revision_limit)
            : null,
        }
      : {}),
    is_required: draft.is_required,
    help_text: draft.help_text.trim() || null,
    options:
      editing?.native_key === 'change_owner_id'
        ? changeOwnerPickerOptionsFromMode(draft.ownerPickerMode)
        : draft.field_type === 'yes_no'
          ? yesNoOptionsFromPair(draft.yesLabel, draft.noLabel)
          : parseOptionsText(draft.optionsText),
    col_span: draft.col_span,
    section_name: draft.section_name.trim() || null,
    is_visible: draft.is_visible,
    default_value:
      draft.field_type === 'yes_no'
        ? storedYesNoValue(
            { options: yesNoOptionsFromPair(draft.yesLabel, draft.noLabel) },
            draft.default_value,
          ) ||
          defaultYesNoStoredValue(
            yesNoChoices({ options: yesNoOptionsFromPair(draft.yesLabel, draft.noLabel) }),
          )
        : draft.default_value.trim() || null,
    ...(allowSkipChecklist ? { skips_auto_checklist: draft.skips_auto_checklist } : {}),
    conditional_field_id: draft.conditional_field_id || null,
    conditional_operator: draft.conditional_field_id ? draft.conditional_operator : null,
    conditional_value: draft.conditional_field_id ? draft.conditional_value : null,
    select_search_enabled:
      draft.field_type === 'list' ? draft.select_search_enabled : false,
  });

  const handleSave = async () => {
    if (!draft.label.trim()) {
      setError('Informe o nome do campo.');
      return;
    }
    if (draft.conditional_field_id && !String(draft.conditional_value || '').trim()) {
      setError('Informe o valor da condição para mostrar esta pergunta.');
      return;
    }
    if (draft.field_type === 'yes_no') {
      const yesLabel = draft.yesLabel.trim();
      const noLabel = draft.noLabel.trim();
      if (!yesLabel || !noLabel) {
        setError('Informe o texto do Sim e o texto do Não.');
        return;
      }
      if (yesLabel.toLowerCase() === noLabel.toLowerCase()) {
        setError('O texto do Sim e do Não precisam ser diferentes.');
        return;
      }
    }
    setSaving(true);
    setError(null);
    try {
      if (creating) {
        await api.createField(payloadFromDraft());
        setMessage('Campo extra incluído.');
      } else if (editing) {
        await api.updateField(editing.id, payloadFromDraft());
        setMessage('Campo atualizado.');
      }
      setCreating(false);
      setEditing(null);
      await load();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível salvar o campo.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (field: ChangeFormField) => {
    const blockReason = changeFormFieldDeleteBlockReason(field);
    if (blockReason) {
      await showAlert({
        title: 'Não é possível excluir',
        message: blockReason,
      });
      return;
    }
    const ok = await showConfirm({
      title: 'Excluir campo',
      message: `Tem certeza que deseja excluir "${field.label}"?`,
      variant: 'danger',
      confirmLabel: 'Excluir',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;
    setSaving(true);
    setError(null);
    try {
      await api.deleteField(field.id);
      setMessage(field.kind === 'extra' ? 'Campo extra removido.' : 'Campo removido do formulário.');
      await load();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível excluir o campo.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = fields.findIndex((item) => item.id === active.id);
    const newIndex = fields.findIndex((item) => item.id === over.id);
    const next = arrayMove(fields, oldIndex, newIndex);
    setFields(next);
    try {
      const data = await api.reorderFields(next.map((item) => item.id));
      setFields(data.items || next);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível gravar a ordem.'));
      await load();
    }
  };

  const availableForCondition = fields
    .filter((item) => !editing || item.id !== editing.id)
    .map((item) => {
      const choices = conditionalChoicesForFormField(
        {
          kind: item.kind,
          native_key: item.native_key,
          field_type: item.field_type,
          options: item.options,
        },
        conditionalCatalogs,
      );
      return {
        id: item.id,
        label: item.label,
        name: item.native_key || `extra_${item.id}`,
        field_type: item.field_type,
        options: (item.options || []).join('\n'),
        conditionalChoices: choices.length ? choices : undefined,
      };
    });

  const conditionParent = fields.find((item) => item.id === draft.conditional_field_id);
  const conditionOperators = operatorsForConditionalFieldType(conditionParent?.field_type);
  const conditionOperatorValue = conditionOperators.some(
    (item) => item.value === draft.conditional_operator,
  )
    ? draft.conditional_operator
    : conditionOperators[0]?.value || 'equals';

  const list = (
    <>
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
          <div>
            {embedded ? null : <h5 className="mb-1">{heading}</h5>}
            <p className="text-muted mb-0">{description}</p>
          </div>
          <Button onClick={openCreate}>Novo campo</Button>
        </div>
        {error ? (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}
        {message ? (
          <Alert variant="success" dismissible onClose={() => setMessage(null)}>
            {message}
          </Alert>
        ) : null}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={(event) => setActiveId(Number(event.active.id))}
          onDragCancel={() => setActiveId(null)}
          onDragEnd={(e) => {
            setActiveId(null);
            void handleDragEnd(e);
          }}
        >
          <SortableContext items={fields.map((item) => item.id)} strategy={verticalListSortingStrategy}>
            {fields.map((field) => (
              <SortableRow
                key={field.id}
                field={field}
                onEdit={openEdit}
                onDelete={(item) => void handleDelete(item)}
              />
            ))}
          </SortableContext>
          <DragOverlay>
            {dragging ? (
              <div className="border rounded p-2 bg-white shadow">
                <strong>{dragging.label}</strong>
                <div className="small text-muted">{fieldMeta(dragging)}</div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
    </>
  );

  return (
    <>
      {embedded ? list : (
        <Card>
          <Card.Body>{list}</Card.Body>
        </Card>
      )}
      <Modal show={creating || Boolean(editing)} onHide={() => { setCreating(false); setEditing(null); }}>
        <Modal.Header closeButton>
          <Modal.Title>{creating ? 'Novo campo extra' : 'Editar campo'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col xs={12}>
              <Form.Label htmlFor="form-field-label">Nome</Form.Label>
              <Form.Control
                id="form-field-label"
                value={draft.label}
                maxLength={255}
                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              />
            </Col>
            {creating || editing?.kind === 'extra' ? (
              <Col xs={12} md={6}>
                <Form.Label htmlFor="form-field-type">Tipo</Form.Label>
                <Form.Select
                  id="form-field-type"
                  aria-label="Tipo"
                  value={draft.field_type}
                  onChange={(e) => {
                    const field_type = e.target.value as ChangeFormFieldType;
                    const yesLabel = draft.yesLabel.trim() || 'Sim';
                    const noLabel = draft.noLabel.trim() || 'Não';
                    const pairOptions = yesNoOptionsFromPair(yesLabel, noLabel);
                    setDraft({
                      ...draft,
                      field_type,
                      period_mode:
                        field_type === 'period' || field_type === 'period_datetime'
                          ? draft.period_mode || 'range'
                          : draft.period_mode,
                      yesLabel,
                      noLabel,
                      default_value:
                        field_type === 'yes_no'
                          ? storedYesNoValue({ options: pairOptions }, draft.default_value) ||
                            defaultYesNoStoredValue(yesNoChoices({ options: pairOptions }))
                          : draft.default_value,
                    });
                  }}
                >
                  {(creating || editing?.kind === 'extra' ? EXTRA_FIELD_TYPES : FIELD_TYPES)
                    .filter((type) =>
                      editing?.native_key === 'requester_manager_group_id'
                        ? type === 'immediate_manager_pick'
                        : type !== 'immediate_manager_pick',
                    )
                    .map((type) => (
                    <option key={type} value={type}>
                      {FORM_FIELD_TYPE_LABELS[type]}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            ) : null}
            {draft.field_type === 'period' || draft.field_type === 'period_datetime' ? (
              <Col xs={12} md={6}>
                <Form.Label>Datas do período</Form.Label>
                <Form.Select
                  value={draft.period_mode}
                  onChange={(e) =>
                    setDraft({ ...draft, period_mode: e.target.value as ChangePeriodMode })
                  }
                >
                  {Object.entries(FORM_PERIOD_MODE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            ) : null}
            {isDateLikeFieldType(draft.field_type) ? (
              <Col xs={12} md={6}>
                <Form.Label>Dia permitido</Form.Label>
                <Form.Select
                  value={draft.weekday_constraint === '' ? '' : String(draft.weekday_constraint)}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      weekday_constraint: e.target.value === '' ? '' : Number(e.target.value),
                    })
                  }
                >
                  <option value="">Qualquer dia</option>
                  {Object.entries(FORM_WEEKDAY_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Se escolher um dia, a data preenchida precisa cair nele. Qualquer dia deixa livre.
                </Form.Text>
              </Col>
            ) : null}
            {allowDateRevisionHistory && isDateLikeFieldType(draft.field_type) ? (
              <Col xs={12} md={6}>
                <Form.Label htmlFor="form-field-date-revision-limit">
                  Limite de alterações da data
                </Form.Label>
                <Form.Control
                  id="form-field-date-revision-limit"
                  type="number"
                  min={0}
                  max={20}
                  value={draft.date_revision_limit === '' ? '' : String(draft.date_revision_limit)}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      date_revision_limit: e.target.value === '' ? '' : Number(e.target.value),
                    })
                  }
                />
                <Form.Text className="text-muted">
                  Vazio ou 0: edição livre, sem guardar valores antigos. Ex.: 3 permite trocar a
                  data três vezes; o valor anterior permanece visível e o novo ocupa o lugar atual.
                </Form.Text>
              </Col>
            ) : null}
            <Col xs={12} md={6}>
              <Form.Label>Largura</Form.Label>
              <Form.Select
                value={draft.col_span}
                onChange={(e) => setDraft({ ...draft, col_span: Number(e.target.value) })}
              >
                <option value={12}>Linha inteira</option>
                <option value={6}>Meia linha</option>
              </Form.Select>
            </Col>
            {hasFormTabs ? (
              <Col xs={12}>
                <Form.Label>Aba</Form.Label>
                <Form.Select
                  aria-label="Aba do campo"
                  value={draft.section_name}
                  onChange={(e) => setDraft({ ...draft, section_name: e.target.value })}
                >
                  <option value="">Formulário principal</option>
                  {formTabs!.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Campos sem aba ficam no formulário principal. Crie novas abas na seção «Abas do
                  formulário» acima.
                </Form.Text>
              </Col>
            ) : formTabs === undefined ? (
              <Col xs={12}>
                <Form.Label>Seção (opcional)</Form.Label>
                <Form.Control
                  value={draft.section_name}
                  maxLength={255}
                  onChange={(e) => setDraft({ ...draft, section_name: e.target.value })}
                />
              </Col>
            ) : null}
            <Col xs={12}>
              <Form.Label>Ajuda</Form.Label>
              <Form.Control
                value={draft.help_text}
                maxLength={500}
                onChange={(e) => setDraft({ ...draft, help_text: e.target.value })}
              />
            </Col>
            {draft.field_type === 'list' ? (
              <Col xs={12}>
                <Form.Check
                  type="switch"
                  id="form-field-select-search"
                  label="Permitir pesquisa no select"
                  checked={draft.select_search_enabled}
                  onChange={(e) =>
                    setDraft({ ...draft, select_search_enabled: e.target.checked })
                  }
                />
                <Form.Text className="text-muted">
                  Útil para listas longas (solicitante, categoria, opções). Solicitante e dono usam busca
                  no servidor; demais campos filtram as opções já carregadas.
                </Form.Text>
              </Col>
            ) : null}
            {editing?.native_key === 'change_owner_id' ? (
              <Col xs={12}>
                <Form.Label htmlFor="form-field-owner-picker">Quem preenche o dono na criação</Form.Label>
                <Form.Select
                  id="form-field-owner-picker"
                  aria-label="Quem preenche o dono na criação"
                  value={draft.ownerPickerMode}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      ownerPickerMode: e.target.value as ChangeOwnerPickerMode,
                    })
                  }
                >
                  {(
                    Object.entries(CHANGE_OWNER_PICKER_MODE_LABELS) as Array<
                      [ChangeOwnerPickerMode, string]
                    >
                  ).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Na criação da mudança, o dono fica travado em quem está logado, salvo se permitir
                  escolher outra pessoa.
                </Form.Text>
              </Col>
            ) : null}
            {draft.field_type === 'list' && (creating || editing?.kind === 'extra') ? (
              <Col xs={12}>
                <Form.Label htmlFor="form-field-options">Opções (uma por linha)</Form.Label>
                <Form.Control
                  id="form-field-options"
                  as="textarea"
                  rows={4}
                  value={draft.optionsText}
                  onChange={(e) => setDraft({ ...draft, optionsText: e.target.value })}
                />
              </Col>
            ) : null}
            {draft.field_type === 'yes_no' && (creating || editing?.kind === 'extra') ? (
              <>
                <Col xs={12} md={6}>
                  <Form.Label htmlFor="form-field-yes-label">Texto do Sim</Form.Label>
                  <Form.Control
                    id="form-field-yes-label"
                    value={draft.yesLabel}
                    maxLength={255}
                    placeholder="Sim"
                    onChange={(e) => {
                      const yesLabel = e.target.value;
                      const options = yesNoOptionsFromPair(yesLabel, draft.noLabel);
                      setDraft({
                        ...draft,
                        yesLabel,
                        default_value:
                          storedYesNoValue({ options }, draft.default_value) ||
                          defaultYesNoStoredValue(yesNoChoices({ options })),
                      });
                    }}
                  />
                  <Form.Text className="text-muted">
                    Label da primeira opção. Pode ser Sim, Aprovado ou o texto que fizer sentido.
                  </Form.Text>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Label htmlFor="form-field-no-label">Texto do Não</Form.Label>
                  <Form.Control
                    id="form-field-no-label"
                    value={draft.noLabel}
                    maxLength={255}
                    placeholder="Não"
                    onChange={(e) => {
                      const noLabel = e.target.value;
                      const options = yesNoOptionsFromPair(draft.yesLabel, noLabel);
                      setDraft({
                        ...draft,
                        noLabel,
                        default_value:
                          storedYesNoValue({ options }, draft.default_value) ||
                          defaultYesNoStoredValue(yesNoChoices({ options })),
                      });
                    }}
                  />
                  <Form.Text className="text-muted">
                    Label da segunda opção. Pode ser Não, Reprovado ou outro texto.
                  </Form.Text>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Label htmlFor="form-field-default">Resposta inicial</Form.Label>
                  <Form.Select
                    id="form-field-default"
                    aria-label="Resposta inicial"
                    value={
                      storedYesNoValue(
                        { options: yesNoOptionsFromPair(draft.yesLabel, draft.noLabel) },
                        draft.default_value,
                      ) ||
                      defaultYesNoStoredValue(
                        yesNoChoices({
                          options: yesNoOptionsFromPair(draft.yesLabel, draft.noLabel),
                        }),
                      )
                    }
                    onChange={(e) => setDraft({ ...draft, default_value: e.target.value })}
                  >
                    {yesNoChoices({
                      options: yesNoOptionsFromPair(draft.yesLabel, draft.noLabel),
                    }).map((choice) => (
                      <option key={choice.value} value={choice.value}>
                        {choice.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Com que resposta a pergunta já nasce. O padrão é o texto do Não.
                  </Form.Text>
                </Col>
              </>
            ) : null}
            <Col xs={12}>
              <Form.Check
                id="form-field-required"
                label="Obrigatório (quando visível)"
                checked={draft.is_required}
                onChange={(e) => setDraft({ ...draft, is_required: e.target.checked })}
              />
            </Col>
            {allowSkipChecklist ? (
              <Col xs={12}>
                <Form.Check
                  id="form-field-skip-checklist"
                  label="Se preenchido, não gera checklist automático e esconde Riscos"
                  checked={draft.skips_auto_checklist}
                  onChange={(e) => setDraft({ ...draft, skips_auto_checklist: e.target.checked })}
                />
              </Col>
            ) : null}
            {editing && !editing.is_locked ? (
              <Col xs={12}>
                <Form.Check
                  id="form-field-visible"
                  label="Visível no formulário"
                  checked={draft.is_visible}
                  onChange={(e) => setDraft({ ...draft, is_visible: e.target.checked })}
                />
              </Col>
            ) : null}
            <Col xs={12}>
              <Form.Label htmlFor="form-field-condition">
                Mostrar esta pergunta apenas se
              </Form.Label>
              <Form.Select
                id="form-field-condition"
                aria-label="Mostrar esta pergunta apenas se"
                value={draft.conditional_field_id}
                onChange={(e) => {
                  const nextId = Number(e.target.value);
                  const parent = fields.find((item) => item.id === nextId);
                  const operators = operatorsForConditionalFieldType(parent?.field_type);
                  setDraft({
                    ...draft,
                    conditional_field_id: nextId,
                    conditional_operator: nextId ? operators[0]?.value || 'equals' : 'equals',
                    conditional_value: '',
                  });
                }}
              >
                <option value={0}>Sempre visível</option>
                {availableForCondition.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))}
              </Form.Select>
              {availableForCondition.length ? (
                <Form.Text className="text-muted">
                  Igual ao cadastro de Compras: esta pergunta só aparece quando a escolhida
                  tiver o valor abaixo. A pergunta de controle precisa já estar salva.
                </Form.Text>
              ) : creating ? (
                <Form.Text className="text-muted">
                  Salve esta pergunta primeiro. Na próxima, escolha aqui esta Sim/Não (ou
                  lista) para mostrar o campo só quando a resposta for Sim, Não ou outra opção.
                </Form.Text>
              ) : (
                <Form.Text className="text-muted">
                  Inclua outra pergunta (Sim/Não ou lista) para poder mostrar esta só quando
                  aquela tiver um valor.
                </Form.Text>
              )}
            </Col>
            {draft.conditional_field_id ? (
              <>
                <Col xs={12} md={6}>
                  <Form.Label htmlFor="form-field-operator">Operador</Form.Label>
                  <Form.Select
                    id="form-field-operator"
                    aria-label="Operador da condição"
                    value={conditionOperatorValue}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        conditional_operator: e.target.value,
                        conditional_value:
                          e.target.value === 'between' ? '{"min":"","max":""}' : '',
                      })
                    }
                  >
                    {conditionOperators.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col xs={12}>
                  <Form.Label>Valor da condição</Form.Label>
                  {conditionParent?.field_type === 'yes_no' ? (
                    <div>
                      {yesNoChoices(conditionParent).map((choice) => (
                        <Form.Check
                          inline
                          key={choice.value}
                          type="radio"
                          id={`form-field-condition-${choice.value}`}
                          name="form-field-condition-yes-no"
                          label={choice.label}
                          checked={
                            storedYesNoValue(conditionParent, draft.conditional_value) ===
                            choice.value
                          }
                          onChange={() => setDraft({ ...draft, conditional_value: choice.value })}
                        />
                      ))}
                      <Form.Text className="text-muted d-block">
                        Esta pergunta aparece quando a escolhida tiver essa resposta.
                      </Form.Text>
                    </div>
                  ) : (
                    <ConditionalValueInput
                      conditionalFieldId={draft.conditional_field_id}
                      availableFields={availableForCondition}
                      value={draft.conditional_value}
                      onChange={(conditional_value) => setDraft({ ...draft, conditional_value })}
                      operator={draft.conditional_operator}
                    />
                  )}
                </Col>
              </>
            ) : null}
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => { setCreating(false); setEditing(null); }}>
            Cancelar
          </Button>
          <Button disabled={saving} onClick={() => void handleSave()}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ChangeFormFieldBuilder;
